import { useState, useEffect } from 'react';
import { makeStyles, Typography, Box } from '@material-ui/core';
import { useSelector } from 'react-redux';
import AccountsTable from '../AddAccountModal/AccountsTable';
import SelectedAccountsList from '../AddAccountModal/SelectedAccountsList';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },
  infoBox: {
    padding: theme.spacing(1.5),
    backgroundColor: '#e8f4fd',
    border: '1px solid #b3d9ff',
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
  infoText: {
    fontSize: 14,
    color: '#1565c0',
    fontWeight: 500,
  },
  gridContainer: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(2),
    minHeight: 400,
    maxHeight: 'calc(70vh - 200px)',
  },
  tableSection: {
    flex: '1 1 65%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(2.5),
  },
  listSection: {
    flex: '1 1 35%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(2.5),
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
  },
}));

const Step3AccountSelection = ({
  selectedAccounts = [],
  onSelectionChange,
  validationErrors = {}
}) => {
  const classes = useStyles();
  const { en: { titles } } = useSelector((state) => state.i18n);
  const { metadata: { currentAccount } } = useSelector((state) => state.user);

  const [localSelectedAccounts, setLocalSelectedAccounts] = useState(selectedAccounts);

  // Sync with parent component
  useEffect(() => {
    setLocalSelectedAccounts(selectedAccounts);
  }, [selectedAccounts]);

  const handleSelectionChange = (newSelection) => {
    setLocalSelectedAccounts(newSelection);
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  const handleRemoveAccount = (accountToRemove) => {
    const newSelection = localSelectedAccounts.filter(
      account => account.uuid !== accountToRemove.uuid &&
                 account.businessName !== accountToRemove.businessName
    );
    handleSelectionChange(newSelection);
  };

  return (
    <div className={classes.container}>
      {/* Info section */}
      <Box className={classes.infoBox}>
        <Typography className={classes.infoText}>
          {titles.selectAccountsInfo || 'Select accounts to grant access to the new user. You can configure specific roles and permissions in the next step.'}
        </Typography>
      </Box>

      {/* Account selection */}
      <div className={classes.gridContainer}>
        {/* Left side - Available Accounts Table */}
        <div className={classes.tableSection}>
          <Typography className={classes.sectionTitle}>
            {titles.availableAccounts || 'Available Accounts'}
          </Typography>
          <AccountsTable
            accountUuid={currentAccount?.uuid}
            selectedAccounts={localSelectedAccounts}
            onSelectionChange={handleSelectionChange}
            isLoading={false}
          />
        </div>

        {/* Right side - Selected Accounts List */}
        <div className={classes.listSection}>
          <Typography className={classes.sectionTitle}>
            {titles.selectedAccounts || 'Selected Accounts'}
          </Typography>
          <SelectedAccountsList
            selectedAccounts={localSelectedAccounts}
            onRemoveAccount={handleRemoveAccount}
            maxHeight="400px"
          />
        </div>
      </div>
    </div>
  );
};

export default Step3AccountSelection;
